import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ReactNode } from "react";
import { cn } from "../ClassName";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import useKeyboardNavigation from "../hooks/useKeyboardNavigation";

interface DropdownSelectorProps<Type> {
  /**
   * @type string
   * @description Tailwind CSS string for custom styling
   */
  className?: string;
  /**
   * @type string
   * @description Tailwind CSS string for custom styling on Dropdown Item.
   */
  dropdownItemStyle?: string;
  /**
   * @type any
   * @description Option that is currently selected.
   */
  selectedOption?: Type;
  /**
   * @type any[]
   * @description Items to select from in the dropdown.
   */
  dropdownItems: Type[] | null;
  /**
   * @type (option: any) => void
   * @description onClick function for when an option is selected from the dropdown
   */
  onClick: (item: Type) => void;
  /**
   * @type (option: any) => any
   * @description A function that returns a key for a given option.
   */
  getKey: (item: Type) => string | number | null;
  /**
   * @type (option: any) => any
   * @description A function that returns the element to render for an option. This
   * allows us the flexibility to customize how an option looks in the dropdown.
   */
  getItemChildren: (item: Type) => React.ReactNode;
  /**
   * @type string
   * @description A string to append to the data-testid of each dropdown item.
   */
  getItemDataTestId?: (item: Type) => string;
  /**
   * @type string
   * @description An optional string to be displayed at the top of the dropdown.
   */
  title?: string;
  /**
   * @type () => void
   * @description A function to toggle the dropdown.
   */
  toggleDropdown?: () => void;

  /**
   * @type HTMLInputElement
   * @description The focused drop down list element.
   */
  dropDownListElement?: HTMLElement | null;
  /**
   * @type ReactNode
   * @description The children to render inside the dropdown selector.
   */
  children?: ReactNode;
}

export function DropdownSelector<Type>(props: DropdownSelectorProps<Type>) {
  const dropDownItems = props.dropdownItems || [];
  const selectedIndex =
    (props.selectedOption &&
      [...(props.dropdownItems || [])].indexOf(props.selectedOption)) ||
    0;
  const selectItemByIndex = (index: number) => {
    if (index >= 0 && index < dropDownItems.length) {
      props.onClick(dropDownItems[index]);
    }
  };
  const [keyboardNavigationState] = useKeyboardNavigation(
    dropDownItems.length,
    selectedIndex,
    selectItemByIndex,
    true,
    props.toggleDropdown || (() => {}),
    props.dropDownListElement,
  );

  return (
    <>
      <div
        className={cn(
          "tw-absolute tw-z-[500] tw-mt-[4px] tw-max-h-[480px] tw-min-w-[180px] tw-max-w-[240px] tw-cursor-pointer tw-overflow-y-auto tw-rounded-[4px] tw-bg-white tw-px-[16px] tw-py-[8px] tw-shadow-[0px_2px_8px_0px_rgba(0,0,0,0.25)]",
          props.className,
        )}
        tabIndex={0}
      >
        {dropDownItems.length > 0 && !props.children ? (
          dropDownItems.map((dropdownItem, index) => (
            <div
              key={props.getKey(dropdownItem)}
              data-testid={
                props.getItemDataTestId
                  ? `dropdown-item-${props.getItemDataTestId(dropdownItem)}`
                  : `dropdown-item-${props.getItemChildren(dropdownItem)}`
              }
              className={`${index === keyboardNavigationState.selectedIndex && `tw-bg-blue-light-1`} -tw-mx-[16px] tw-flex tw-min-h-[40px] tw-items-center tw-justify-between tw-gap-4 tw-overflow-hidden tw-px-[16px] hover:tw-bg-gray-light-4`}
              onMouseDown={() => {
                props.onClick(dropdownItem);
              }}
            >
              {props.getItemChildren(dropdownItem)}
              {props.selectedOption === dropdownItem && (
                <FontAwesomeIcon icon={faCheck} className="tw-text-blue-1" />
              )}
            </div>
          ))
        ) : (
          <div>{props.children}</div>
        )}
      </div>
    </>
  );
}
